import { PlusCircleOutlined, SyncOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import type { ViewQueryList } from '@salescore/core'
import { Button } from 'antd'
import { isNotUndefined } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column.ts'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import { useMoveToRowIndexAndEditModeMutation } from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import { useLoadingState } from '../../../recoil/records/hooks'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation'
import { useRefetchMutation } from '../../../recoil/records/mutations/useRefetchMutation'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { ROW_HEIGHT } from '../body/const'

export const RSheetsFooterRow = ({
  totalWidth,
  columns,
  view,
  ability,
  query,
}: {
  totalWidth: number
  columns: Array<RSheetColumn<RSheetRecordNode>>
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList
}): ReactElement => {
  const recordsMutation = useRefetchMutation(view, query)
  const me = useMe()
  const { ref, inView } = useInView({ threshold: 0 })
  const viewQueryResult = useViewQueryResult(view)
  const loading = useLoadingState()
  const moveToRowIndexAndEditModeMutation = useMoveToRowIndexAndEditModeMutation(columns, ability)
  const { addRootRecord } = useAddEmptyRecordMutation(query, ability)
  // eslint-disable-next-line sonarjs/todo-tag
  const rows = useRecordsImpl() // TODO: onClick相当をmutationにしたい。

  // footerがビューに入ったら追加読み込み
  useEffect(() => {
    if (inView && loading.isFalse) {
      void recordsMutation.loadNext()
    }
  }, [inView, rows.length, loading.isFalse])

  const isLoadable = isNotUndefined(viewQueryResult?.nextCursor)

  // inViewのロジックは引き続き使うが、UIコンポーネントは使わない
  if (me.organization.isEarlyReleaseTarget) {
    if (isLoadable) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: style
      return (
        <div
          ref={ref}
          style={{
            width: totalWidth,
          }}
          onClick={() => {
            if (loading.isFalse) {
              void recordsMutation.loadNext()
            }
          }}
          data-e2e="rsheets-footer-row"
        >
          <Button
            loading={loading.isTrue}
            icon={<SyncOutlined />}
            type="text"
            disabled={loading.isTrue}
            style={{
              color: '#555',
            }}
          >
            {t(`さらに読み込み`)}
          </Button>
        </div>
      )
    }
    return <div />
  }

  return (
    <div
      data-e2e="rsheets-footer-row"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
      onClick={async () => {
        await addRootRecord({ rowIndex: rows.length - 1 })
        moveToRowIndexAndEditModeMutation(rows.length)
      }}
      style={{
        backgroundColor: 'white',
        cursor: 'pointer',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: ROW_HEIGHT,
        width: '100vw',
        border: `1px #EDF1F2 solid`,
      }}
    >
      {isLoadable && (
        <div
          ref={ref}
          onClick={() => {
            if (loading.isFalse) {
              void recordsMutation.loadNext()
            }
          }}
        >
          <Button
            loading={loading.isTrue}
            icon={<SyncOutlined />}
            type="text"
            disabled={loading.isTrue}
            style={{
              color: '#555',
            }}
          >
            {t(`さらに読み込み`)}
          </Button>
        </div>
      )}
      <div
        style={{
          left: 20,
          position: 'absolute',
          color: '#999',
        }}
      >
        <PlusCircleOutlined /> {t(`追加`)}
      </div>
    </div>
  )
}
